import { DOCUMENT } from '@angular/common';
import { Injectable, inject, signal } from '@angular/core';

import type { BrandingClientSet } from '@mimecast-ui/branding/data-access';
import {
  applyBrandingFavicon,
  applyBrandingStylesheet,
  resolveBrandingSetFromUrl,
  setBrandingOnWindow,
  updateBrandingCssVars,
} from '@mimecast-ui/branding/data-access/script';

import { applyBrandingPageTitle } from '../utils/ul-branding.utils';

const UL_CONTEXT_PATH = 'u';

@Injectable({ providedIn: 'root' })
export class UniversalLoginBrandingService {
  private readonly document = inject(DOCUMENT);

  private readonly brandingState = signal<BrandingClientSet | null>(null);
  readonly branding = this.brandingState.asReadonly();

  private initializePromise: Promise<void> | null = null;

  getBranding(): BrandingClientSet | null {
    return this.brandingState();
  }

  initializeFromUrl(pageUrl: string): Promise<void> {
    if (!this.initializePromise) {
      this.initializePromise = this.loadBranding(pageUrl);
    }

    return this.initializePromise;
  }

  private async loadBranding(pageUrl: string): Promise<void> {
    const { brandingSet, shouldPersistToWindow, contextPath } =
      await resolveBrandingSetFromUrl(pageUrl, {
        defaultContextPath: UL_CONTEXT_PATH,
      });

    if (shouldPersistToWindow) {
      this.persistBrandingSet(contextPath, brandingSet);
      return;
    }

    this.applyBrandingSet(brandingSet);
  }

  private persistBrandingSet(
    contextPath: string,
    brandingSet: BrandingClientSet
  ): void {
    setBrandingOnWindow(globalThis, {
      ...brandingSet,
      originalContextPath: contextPath,
    });
    this.applyBrandingSet(brandingSet);
  }

  private applyBrandingSet(brandingSet: BrandingClientSet): void {
    updateBrandingCssVars(this.document, brandingSet);
    applyBrandingFavicon(this.document, brandingSet);
    applyBrandingPageTitle(this.document, brandingSet);
    applyBrandingStylesheet(this.document, brandingSet);
    this.brandingState.set(brandingSet);
  }
}
